<script lang="ts" setup>
import type { IArticleCategory } from '~/api/article';
import Side from '../../components/Side.vue';
import Lists from './components/Lists.vue';
import useSeo from './seo';

const route = useRoute()
const { id } = route.params
const { article } = useApi()
const articleStore = useArticleStore()
const categories = computed(() => articleStore.ecoCategories)
if (categories.value.length === 0) {
  const { data: categoryData } = await article.getArticleCategories('articleEcolife')
  articleStore.setArticleCategories('eco', categoryData.value?.data as IArticleCategory[])
}
const { data: articleData } = await article.getArticleEcolife(id as string)
const articleRes = computed(() => articleData.value?.data)
const { data: recommendData } = article.getArticleRecommends(id as string)
const recommendRes = computed(() => recommendData.value?.data)

useSeo(articleRes.value)
</script>

<template>
  <section class="section">
    <div class="section__left">
      <div class="section__left__inner">
        <Side :data="categories" :current-category-id="articleRes?.categories?.[0]?.id" />
        <Share class="section__share pc" :border="false" color="blue3" :round="true" />
      </div>
    </div>
    <div class="section__right" v-if="articleRes">
      <figure class="section__banner">
        <Nimg :src="articleRes?.image?.link" :alt="articleRes?.image?.alt" :title="articleRes?.image?.title" />
      </figure>
      <div class="section__title">{{ articleRes.subject }}</div>
      <div class="section__date">{{ articleRes.publish_date }}</div>
      <div class="content__box">
        <Block :blocks="articleRes?.blocks" />
        <BlockGBlockTag :tags="articleRes?.tags" />
      </div>
      <Share class="section__share mobile" :border="false" color="blue3" :round="true" />
    </div>
    <div class="section__bottom" v-if="recommendRes">
      <TitleTitle2 class="section__bottom__title" :data="{ en: 'read more', tw: '相關推薦' }" />
      <Lists :data="recommendRes" />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.section {
  @include grid;

  @include max-media(960) {
    @include grid(4, 24);
  }

  &__left {
    position: relative;
    grid-column: 1 / span 2;

    @include min-media(961) {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    @include max-media(960) {
      grid-column: 1 / span 4;
    }

    &__inner {
      @include min-media(961) {
        position: fixed;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }
    }
  }

  &__right {
    grid-column: 4 / span 8;

    @include max-media(960) {
      grid-column: 1 / span 4;
    }
  }

  &__share {
    &.pc {
      margin-top: toRem(24);

      @include max-media(960) {
        display: none;
      }
    }

    &.mobile {
      display: flex;
      justify-content: center;
      margin-bottom: toRem(70);

      @include min-media(961) {
        display: none;
      }
    }
  }

  &__banner {
    width: 100%;
    margin-bottom: toRem(24);

    img {
      display: block;
      width: 100%;
    }
  }

  &__title {
    margin-bottom: toRem(24);
    font-size: toRem(28);
    font-weight: 500;

    @include max-media(640) {
      font-size: toRem(24);
    }
  }

  &__date {
    margin-bottom: toRem(24);
    font-size: toRem(16);
    color: $blue3;
    line-height: 1.45;
    text-align: center;

    @include max-media(640) {
      font-size: toRem(14);
    }
  }

  &__bottom {
    position: relative;
    z-index: 2;
    grid-column: 1 / span 12;
    background-color: $color-white-2;

    @include max-media(960) {
      grid-column: 1 / span 4;
    }

    &__title {
      margin-bottom: toRem(56);

      @include max-media(480) {
        margin-bottom: toRem(32);
      }
    }
  }

  :deep(.title2__en) {
    color: $blue3;

    @include min-media(641) {
      font-size: toRem(80);
    }
  }
}
</style>
